import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import Input from '~/components/common/Input'
import { ModalBody, ModalFooter, ModalHeaderContent } from '~/components/modals/CurieModal'
import { ProgressBar } from '~/components/common/progress-bar/ProgressBar'
import { Selector, SelectItem } from '~/components/common/Selector'
import { SiteLayoutV2 } from '~/components/layout/SiteLayoutV2'
import { IconCheckFilled, IconCross } from '~/components/icons'
import {
  isPublicEmailDomain,
  setupTenant,
  verifyTenant,
  type TenantSetupRequest
} from '~/services/tenantService'
import { BetterToast } from '~/utils/toast'

type SetupStep = 'configure' | 'verify' | 'complete'

const AzureIcon = () => (
  <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none'>
    <path
      d='M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z'
      fill='#00BCF2'
    />
  </svg>
)

const SSOSetupIcon = () => (
  <svg className='w-6 h-6' viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9H19V21H5V3H13V9H21ZM12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8ZM12 14C14.33 14 19 15.34 19 18V20H5V18C5 15.34 9.67 15 12 15ZM12 15.9C9.03 15.9 6.9 17.36 6.9 18V18.1H17.1V18C17.1 17.36 14.97 15.9 12 15.9Z'
      fill='#846FE1'
    />
  </svg>
)

const Page: FC = () => {
  const [domain, setDomain] = useState('')
  const [email, setEmail] = useState('')
  const [currentStep, setCurrentStep] = useState<SetupStep>('configure')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Form fields
  const [orgName, setOrgName] = useState('')
  const [providerType, setProviderType] = useState<string>('microsoft')

  // Microsoft fields
  const [microsoftClientId, setMicrosoftClientId] = useState('')
  const [microsoftClientSecret, setMicrosoftClientSecret] = useState('')
  const [microsoftTenantId, setMicrosoftTenantId] = useState('')

  // Okta fields
  const [oktaDomain, setOktaDomain] = useState('')
  const [oktaClientId, setOktaClientId] = useState('')
  const [oktaClientSecret, setOktaClientSecret] = useState('')

  // Google fields
  const [googleClientId, setGoogleClientId] = useState('')
  const [googleClientSecret, setGoogleClientSecret] = useState('')

  // SAML fields
  const [samlMetadataUrl, setSamlMetadataUrl] = useState('')
  const [samlProviderName, setSamlProviderName] = useState('')

  // Setup results
  const [verificationToken, setVerificationToken] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [hostedUiUrl, setHostedUiUrl] = useState('')
  const [idpRedirectUri, setIdpRedirectUri] = useState('')

  // Read state from URL query parameters, redirect if no domain
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlDomain = urlParams.get('domain')

    if (!urlDomain) {
      navigate('/signin')
      return
    }

    setDomain(urlDomain)
    const urlStep = urlParams.get('step') as SetupStep | null
    const urlTenantId = urlParams.get('tenantId')
    const urlVerificationToken = urlParams.get('verificationToken')
    const urlIdpRedirectUri = urlParams.get('idpRedirectUri')
    const urlHostedUiUrl = urlParams.get('hostedUiUrl')

    if (urlStep && ['configure', 'verify', 'complete'].includes(urlStep)) {
      setCurrentStep(urlStep)
    }
    if (urlTenantId) setTenantId(urlTenantId)
    if (urlVerificationToken) setVerificationToken(urlVerificationToken)
    if (urlIdpRedirectUri) setIdpRedirectUri(urlIdpRedirectUri)
    if (urlHostedUiUrl) setHostedUiUrl(urlHostedUiUrl)
  }, [])

  // Update URL when state changes
  const updateUrl = (updates: Record<string, string>) => {
    const urlParams = new URLSearchParams(window.location.search)

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        urlParams.set(key, value)
      } else {
        urlParams.delete(key)
      }
    })

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      BetterToast.success('Copied to clipboard!')
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      BetterToast.success('Copied to clipboard!')
    }
  }

  const handleSetupSubmit = async () => {
    try {
      console.log('handleSetupSubmit')
      setErrorMessage('')
      console.log('handleSetupSubmit 2')

      if (!domain) {
        setErrorMessage('Domain is required')
        return
      }

      // Prevent SSO setup for public email domains
      if (isPublicEmailDomain(domain)) {
        setErrorMessage(
          "SSO cannot be configured for public email domains like Gmail, Outlook, Yahoo, etc. Please use your organization's domain."
        )
        return
      }
      console.log('handleSetupSubmit 3')

      const setupRequest: TenantSetupRequest = {
        domain,
        provider_type: providerType,
        org_name: orgName || undefined
      }

      // Add provider-specific fields
      if (providerType === 'microsoft') {
        if (!microsoftClientId || !microsoftClientSecret || !microsoftTenantId) {
          setErrorMessage('All Microsoft fields are required')
          return
        }
        setupRequest.microsoft_client_id = microsoftClientId
        setupRequest.microsoft_client_secret = microsoftClientSecret
        setupRequest.microsoft_tenant_id = microsoftTenantId
      } else if (providerType === 'okta') {
        if (!oktaDomain || !oktaClientId || !oktaClientSecret) {
          setErrorMessage('All Okta fields are required')
          return
        }
        setupRequest.okta_domain = oktaDomain
        setupRequest.okta_client_id = oktaClientId
        setupRequest.okta_client_secret = oktaClientSecret
      } else if (providerType === 'google') {
        if (!googleClientId || !googleClientSecret) {
          setErrorMessage('All Google fields are required')
          return
        }
        setupRequest.google_client_id = googleClientId
        setupRequest.google_client_secret = googleClientSecret
      } else if (providerType === 'saml') {
        if (!samlMetadataUrl) {
          setErrorMessage('SAML metadata URL is required')
          return
        }
        setupRequest.saml_metadata_url = samlMetadataUrl
        setupRequest.saml_provider_name = samlProviderName || undefined
      }

      setIsLoading(true)
      const result = await setupTenant(setupRequest)

      if (result.success) {
        setVerificationToken(result.verification_token || '')
        setTenantId(result.tenant_id || '')
        if (result.cognito_config?.hosted_ui_url) {
          setHostedUiUrl(result.cognito_config.hosted_ui_url)
        }
        if (result.cognito_config?.idp_redirect_uri) {
          setIdpRedirectUri(result.cognito_config.idp_redirect_uri)
        }
        setCurrentStep('verify')

        // Update URL with state
        updateUrl({
          domain: domain,
          step: 'verify',
          tenantId: result.tenant_id || '',
          verificationToken: result.verification_token || '',
          idpRedirectUri: result.cognito_config?.idp_redirect_uri || '',
          hostedUiUrl: result.cognito_config?.hosted_ui_url || ''
        })

        BetterToast.success('SSO configuration created successfully!')
      } else {
        setErrorMessage(result.error || 'Failed to setup SSO')
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to setup SSO')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifySubmit = async () => {
    if (!domain || !verificationToken) {
      setErrorMessage('Missing domain or verification token')
      return
    }

    try {
      setIsLoading(true)
      const result = await verifyTenant(domain, {
        domain,
        verification_token: verificationToken
      })

      if (result.success) {
        if (result.hosted_ui_url) {
          setHostedUiUrl(result.hosted_ui_url)
        }
        setCurrentStep('complete')

        // Update URL with state
        updateUrl({
          domain: domain,
          step: 'complete',
          tenantId: tenantId,
          hostedUiUrl: result.hosted_ui_url || hostedUiUrl
        })

        BetterToast.success('SSO configuration verified and activated!')
      } else {
        setErrorMessage(result.error || 'Verification failed')
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const navigate = useNavigate()
  const handleBackToSignIn = () => {
    navigate('/signin')
  }

  const renderProviderFields = () => {
    switch (providerType) {
      case 'microsoft':
        return (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client ID<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Microsoft Client ID'
                value={microsoftClientId}
                onChange={e => setMicrosoftClientId(e.target.value)}
                className='h-[48px]'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client Secret<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Microsoft Client Secret'
                value={microsoftClientSecret}
                onChange={e => setMicrosoftClientSecret(e.target.value)}
                className='h-[48px]'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Tenant ID<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Microsoft Tenant ID'
                value={microsoftTenantId}
                onChange={e => setMicrosoftTenantId(e.target.value)}
                className='h-[48px]'
              />
            </div>
          </div>
        )
      case 'okta':
        return (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Okta Domain<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='e.g., dev-123456.okta.com or mycompany.okta.com'
                value={oktaDomain}
                onChange={e => setOktaDomain(e.target.value)}
                className='h-[48px]'
              />
              <p className='text-xs text-tokens-neutral-text-tertiary mt-1 select-none'>
                Your Okta domain
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client ID<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Okta Client ID'
                value={oktaClientId}
                onChange={e => setOktaClientId(e.target.value)}
                className='h-[48px]'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client Secret<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Okta Client Secret'
                value={oktaClientSecret}
                onChange={e => setOktaClientSecret(e.target.value)}
                className='h-[48px]'
              />
            </div>
          </div>
        )
      case 'google':
        return (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client ID<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Google Client ID'
                value={googleClientId}
                onChange={e => setGoogleClientId(e.target.value)}
                className='h-[48px]'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Client Secret<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter Google Client Secret'
                value={googleClientSecret}
                onChange={e => setGoogleClientSecret(e.target.value)}
                className='h-[48px]'
              />
            </div>
          </div>
        )
      case 'saml':
        return (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Metadata URL<span className='text-tokens-status-error-1'>*</span>
              </label>
              <Input
                placeholder='Enter SAML Metadata URL'
                value={samlMetadataUrl}
                onChange={e => setSamlMetadataUrl(e.target.value)}
                className='h-[48px]'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
                Provider Name (Optional)
              </label>
              <Input
                placeholder='Enter SAML Provider Name'
                value={samlProviderName}
                onChange={e => setSamlProviderName(e.target.value)}
                className='h-[48px]'
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const renderConfigureStep = () => (
    <div className='w-[320px] mx-auto mt-[48px] space-y-6'>
      <div>
        <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
          Organization Name (Optional)
        </label>
        <Input
          placeholder='Enter your organization name'
          value={orgName}
          onChange={e => setOrgName(e.target.value)}
          className='h-[48px]'
        />
      </div>

      <div>
        <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
          Identity Provider<span className='text-tokens-status-error-1'>*</span>
        </label>
        <Selector
          variant='labeled'
          placeholder='Select identity provider'
          value={providerType}
          onSelectionChange={setProviderType}
          className='w-full'
        >
          <SelectItem value='microsoft'>Microsoft Azure AD</SelectItem>
          <SelectItem value='okta'>Okta</SelectItem>
          <SelectItem value='google'>Google Workspace</SelectItem>
          <SelectItem value='saml'>SAML 2.0</SelectItem>
        </Selector>
      </div>

      {renderProviderFields()}

      {errorMessage && (
        <div className='flex items-center text-tokens-status-error-1 text-sm select-none'>
          <IconCross className='mr-1' />
          {errorMessage}
        </div>
      )}
    </div>
  )

  const renderVerifyStep = () => (
    <div className='mx-auto mt-[48px] flex flex-col gap-3'>
      <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
        <div className='flex items-center text-green-800 text-sm mb-2 select-none'>
          <IconCheckFilled className='mr-2' />
          <strong>Configuration Complete</strong>
        </div>
        <p className='text-green-700 text-sm select-none'>
          Your SSO configuration has been created successfully.
          {tenantId && (
            <span className='block mt-1'>
              <span className='select-none'>Tenant ID: </span>
              <code className='bg-green-100 px-1 rounded select-text'>{tenantId}</code>
            </span>
          )}
        </p>
      </div>

      {idpRedirectUri && (
        <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <h3 className='font-semibold text-blue-800 mb-2 select-none'>
            {providerType === 'microsoft'
              ? 'Azure AD App Registration - Redirect URI'
              : providerType === 'okta'
                ? 'Okta App Configuration - Redirect URI'
                : 'Identity Provider Configuration - Redirect URI'}
          </h3>
          <p className='text-blue-700 text-sm mb-3 select-none'>
            {providerType === 'microsoft'
              ? 'Add this redirect URI to your Azure AD app registration:'
              : providerType === 'okta'
                ? 'Add this redirect URI to your Okta app configuration:'
                : 'Add this redirect URI to your identity provider configuration:'}
          </p>
          <div className='flex items-center space-x-2'>
            <code className='flex-1 bg-blue-100 px-3 py-2 rounded text-sm font-mono text-blue-900 break-all select-text'>
              {idpRedirectUri}
            </code>
            <button
              type='button'
              onClick={() => copyToClipboard(idpRedirectUri)}
              className='px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors'
              title='Copy redirect URI'
            >
              Copy
            </button>
          </div>
          <p className='text-blue-600 text-xs mt-2 select-none'>
            {providerType === 'microsoft'
              ? 'Navigate to Azure Portal → App registrations → Your app → Authentication → Add this URL to "Redirect URIs"'
              : providerType === 'okta'
                ? 'Navigate to Okta Admin Console → Applications → Your app → General → Edit → Add this URL to "Sign-in redirect URIs"'
                : "Add this URL to your identity provider's redirect URI configuration"}
          </p>
        </div>
      )}

      {errorMessage && (
        <div className='flex items-center text-tokens-status-error-1 text-sm select-none'>
          <IconCross className='mr-1' />
          {errorMessage}
        </div>
      )}
    </div>
  )

  const renderCompleteStep = () => (
    <div className='mx-auto mt-[48px] flex flex-col gap-3'>
      <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
        <div className='flex items-center text-green-800 text-sm mb-2 select-none'>
          <IconCheckFilled className='mr-2' />
          <strong>SSO is Active</strong>
        </div>
        <p className='text-green-700 text-sm select-none'>
          Users with {domain} email addresses can now sign in using SSO.
        </p>
      </div>

      <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
        <h3 className='font-semibold text-blue-800 mb-2 select-none'>What's Next:</h3>
        <ul className='list-disc list-inside text-blue-700 text-sm space-y-1 select-none'>
          <li>Share the sign-in page with your team members</li>
          <li>Test SSO login with your organization accounts</li>
          <li>Users can now enter their email on the sign-in page to see SSO options</li>
        </ul>
      </div>
    </div>
  )

  const getCurrentStepNumber = () => {
    switch (currentStep) {
      case 'configure':
        return 1
      case 'verify':
        return 2
      case 'complete':
        return 3
      default:
        return 1
    }
  }

  return (
    <SiteLayoutV2 titleClassName='hidden'>
      <div className='flex justify-center items-start w-full h-auto pb-4 mt-20'>
        <div
          className='w-full min-w-[500px] max-w-[600px] bg-white rounded-[28px] mx-8 overflow-hidden'
          style={{
            boxShadow:
              '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
          <ModalBody>
            <ProgressBar currentStep={getCurrentStepNumber()} totalSteps={3} />
            <div style={{ marginTop: 60 }}>
              <ModalHeaderContent icon={<SSOSetupIcon />} title='SSO Setup' />
            </div>

            {currentStep === 'configure' && renderConfigureStep()}
            {currentStep === 'verify' && renderVerifyStep()}
            {currentStep === 'complete' && renderCompleteStep()}
          </ModalBody>
          <ModalFooter className='mt-6'>
            {currentStep === 'configure' && (
              <Button variant='outlined' onClick={handleBackToSignIn}>
                Cancel
              </Button>
            )}
            {currentStep === 'configure' && (
              <Button onClick={handleSetupSubmit} isDisabled={isLoading}>
                {isLoading ? 'Setting up...' : 'Set up SSO'}
              </Button>
            )}
            {currentStep === 'verify' && (
              <Button onClick={handleVerifySubmit} isDisabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify & Activate'}
              </Button>
            )}
            {currentStep === 'complete' && (
              <Button onClick={handleBackToSignIn}>Go to Sign In</Button>
            )}
          </ModalFooter>
        </div>
      </div>
    </SiteLayoutV2>
  )
}

export default Page
